import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import JsonLd from './components/JsonLd';
import {
  SITE_URL,
  formatPageTitle,
  formatDescription,
  buildOrganizationSchema,
  buildWebSiteSchema,
  buildWebPageSchema,
  buildSchemaGraph,
} from './utils/seo';
import { useDarkMode } from './DarkModeContext';
import './MerraComparison.css';
import Footer from './Footer';

function MerraComparison() {
    const { darkMode } = useDarkMode();
    
    const comparisonData = [
        {
            tool: 'HeyGen',
            purpose: 'Avatar & lip-sync videos',
            needs: 'Script text only (Optional: cloning yourself)',
            output: 'Talking-head explainer',
            friction: "It's not you, it's an avatar"
        },
        {
            tool: 'Descript',
            purpose: 'Podcast & A-roll editing',
            needs: 'Recorded dialogue',
            output: 'Clean edits & transcripts',
            friction: 'Manual, time-heavy workflow'
        },
        {
            tool: 'OpusClip',
            purpose: 'Auto-clipping talking-head content',
            needs: 'Long A-roll videos',
            output: 'Highlight reels for creators',
            friction: 'Needs camera presence'
        },
        {
            tool: 'CapCut',
            purpose: 'Manual short-form editing',
            needs: 'Full creative control',
            output: 'Any sort of video',
            friction: 'Requires editing skills & time'
        },
        {
            tool: 'Merra',
            purpose: 'AI storytelling from your b-rolls',
            needs: 'Existing brand footage + short brief',
            output: 'Scripted, voiced & edited automatically',
            friction: 'None, no camera, no timeline',
            highlight: true
        }
    ];
    
    return (
        <>
        <Helmet>
            <title>{formatPageTitle('Merra vs HeyGen, Descript, OpusClip & CapCut')}</title>
            <meta name="description" content={formatDescription('Compare Merra AI with HeyGen, Descript, OpusClip, and CapCut. See features, use cases, and which AI video tool fits your workflow.')} />
            <meta name="keywords" content="Merra comparison, Merra vs HeyGen, Merra vs Descript, AI video tool comparison, video editing tools" />
            <link rel="canonical" href="https://www.merra.ai/merra-comparison" />
            <meta property="og:title" content={formatPageTitle('Merra vs HeyGen, Descript, OpusClip & CapCut')} />
            <meta property="og:description" content={formatDescription('Compare Merra AI with HeyGen, Descript, OpusClip, and CapCut. Find the right AI video tool for your needs.')} />
            <meta property="og:url" content="https://www.merra.ai/merra-comparison" />
        </Helmet>
        <JsonLd
            schema={buildSchemaGraph([
                buildOrganizationSchema(),
                buildWebSiteSchema(),
                buildWebPageSchema({
                    name: formatPageTitle('Merra vs HeyGen, Descript, OpusClip & CapCut'),
                    description: 'Compare Merra AI with other AI video tools.',
                    url: `${SITE_URL}/merra-comparison`,
                }),
            ])}
        />
        <div className={`merra-comparison ${darkMode ? 'dark' : ''}`}>
            <div className="merra-comparison-container">
                <div className="merra-comparison-header">
                    <Link to="/" className="back-link">
                        ← Back to Home
                    </Link>
                </div>

                <article className="merra-comparison-article">
                    <h1 className="merra-comparison-title">
                        🧠 What Makes Merra Different from HeyGen, Descript, OpusClip, or CapCut
                    </h1>
                    
                    <p className="merra-comparison-intro">
                        Not all AI video tools serve the same purpose.
                    </p>
                    
                    <p className="merra-comparison-intro">
                        While some help creators edit podcasts or talking-heads, <strong>Merra is built for brands that already have footage</strong>, and want to turn it into ready-to-post short-form videos automatically.
                    </p>

                    <div className="merra-comparison-definitions">
                        <h2 className="definitions-title">🎥 Quick Definitions</h2>
                        <div className="definition-item">
                            <strong>A-roll:</strong> the main footage — someone talking directly to the camera (interviews, tutorials, podcasts). Each A-roll usually belongs to one specific message or video.
                        </div>
                        <div className="definition-item">
                            <strong>B-roll:</strong> the filler or supporting footage — shots of products, hands, waves, events, places, or people in action. B-rolls aren't tied to one story, so they can be reused across dozens of videos to illustrate new scripts or messages.
                        </div>
                    </div>

                    <div className="comparison-table-wrapper">
                        <table className="comparison-table">
                            <thead>
                                <tr>
                                    <th>Tool</th>
                                    <th>What it's built for</th>
                                    <th>What it needs</th>
                                    <th>Output style</th>
                                    <th>Biggest friction</th>
                                </tr>
                            </thead>
                            <tbody>
                                {comparisonData.map((row, index) => (
                                    <tr key={index} className={row.highlight ? 'highlight-row' : ''}>
                                        <td className="tool-name">
                                            <strong>{row.tool}</strong>
                                        </td>
                                        <td>{row.purpose}</td>
                                        <td>{row.needs}</td>
                                        <td>{row.output}</td>
                                        <td>{row.friction}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="merra-comparison-summary">
                        <h2>In short:</h2>
                        <ul className="summary-list">
                            <li><strong>HeyGen</strong> makes avatars talk.</li>
                            <li><strong>Descript</strong> polishes podcasts.</li>
                            <li><strong>OpusClip</strong> cuts interviews.</li>
                            <li><strong>CapCut</strong> edits manually.</li>
                            <li><strong>Merra</strong> tells stories with the footage you already have.</li>
                        </ul>
                    </div>

                    <div className="merra-comparison-note">
                        <p>
                            <em>
                                For simplicity, we're skipping projects like Sora, Veo 3, or NanoBanana, those are cinematic text-to-video models, not tools for daily brand content.
                            </em>
                        </p>
                        <p>
                            <strong>Merra isn't about generating entire worlds from prompts, it's about repurposing what's real into what's relevant.</strong>
                        </p>
                    </div>
                </article>
            </div>
            <Footer />
        </div>
        </>
    );
}

export default MerraComparison;
